import { DataManager, WebApiAdaptor, Query } from '@syncfusion/ej2-data';
import { GetBackendUrlForGlobalFunctions } from './../App';
import { useCallback } from 'react';
import { useOrgCodeMap, getSelectedOrgIds } from '../utils/orgCodeMap';
import './GlobalDropDown.css';
import {
  CheckBoxSelection,
  DropDownListComponent,
  FieldSettingsModel,
  Inject,
  MultiSelectComponent,
} from '@syncfusion/ej2-react-dropdowns';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCloudSchedulerFilterTeamDropdownValueChanged,
  setIsEventsUpdated,
  setTeamDataChanged,
} from '../redux/search.slice';
import Cookies from "js-cookie";
import {store} from "../store";
interface IPropsDD {
  url: string;
  field: FieldSettingsModel;
  placeHolder?: string;
  id?: string;
  refDD?: (ref: DropDownListComponent) => void;
  value?: any;
  onChange?: (e: any) => void;
  itemTemplate?: any;
  valueTemplate?: any;
  text?: string;
  selected?: (e: any) => void;
  floatLabelType?: 'Auto' | 'Always' | 'Never';
  enablePersistence?: boolean;
  actionComplete?: (e: any) => void;
  filterByActiveOrg?: boolean;
}
interface IPropsMulti {
  url: string;
  field: FieldSettingsModel;
  placeHolder?: string;
  id?: string;
  refDD?: (ref: MultiSelectComponent) => void;
  value?: Array<any>;
  onChange?: (e: any) => void;
  filtering?: (e: any) => void;
  query?: Query;
  allowFiltering?: boolean;
  maximumSelectionLength?: number;
  itemTemplate?: (args: any) => JSX.Element;
  floatLabelType?: 'Auto' | 'Always' | 'Never';
  enablePersistence?: boolean;
  refresh?: boolean;
  actionComplete?: (e: any) => void;
  close?: (args?: any) => void;
  remoteFilter?: boolean;
}
const optionsCache = new Map<string, Promise<any[]>>();
export const invalidateGlobalFilterCache = (query: string) => {
  let removed = 0;
  Array.from(optionsCache.keys()).forEach((key) => {
    if (key.includes(`where=${query}$`)) {
      optionsCache.delete(key);
      removed++;
    }
  });
  console.log('[globalFilterCache] invalidated', query, 'removed:', removed);
};
const selectedGroupsStore = new Map<string, Map<string, Set<string>>>();
export const GlobalDropDown = ({
                                 field,
                                 url,
                                 id,
                                 placeHolder,
                                 refDD,
                                 onChange,
                                 value,
                                 itemTemplate,
                                 text,
                                 valueTemplate,
                                 selected,
                                 floatLabelType,
                                 actionComplete,
                                 filterByActiveOrg = false,
                               }: IPropsDD) => {
  const token = Cookies.get('token');
  const state = store.getState();
  const backEndUrl = state.search.backEndUrl;
  const data = new DataManager({
    adaptor: new WebApiAdaptor(),
    crossDomain: true,
    url: backEndUrl + url,
    headers: [{
      Authorization: `Bearer ${token}`,
      contentType: 'application/json',
    }],
  });

  const dispatch = useDispatch();
  const isEventsUpdated = useSelector(
      (state: any) => state.search.isEventsUpdated
  );

  const orgMap = useOrgCodeMap();
  const isOrgGroup = field?.groupBy === 'organization_id';
  const needsFetch = filterByActiveOrg || isOrgGroup;
  const [selectedOrgIds, setSelectedOrgIdsState] = useState<number[]>(() => getSelectedOrgIds());

  useEffect(() => {
    const handleOrgChanged = (e: any) => {
      const ids: number[] = e?.detail?.selectedOrgIds ?? getSelectedOrgIds();
      setSelectedOrgIdsState(ids);
    };
    window.addEventListener('organizationChanged', handleOrgChanged);
    return () => window.removeEventListener('organizationChanged', handleOrgChanged);
  }, []);

  const [dataState, setDataState] = useState<any>(needsFetch ? [] : data);

  const collapsedRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (!needsFetch) return;
    let alive = true;
    const activeOrgIds = selectedOrgIds;
    data
        .executeQuery(new Query())
        .then((res: any) => {
          const items: any[] = res?.result || [];
          const filtered = filterByActiveOrg && activeOrgIds.length
              ? items.filter((item: any) => {
                const ids: number[] =
                    Array.isArray(item.organization_ids) && item.organization_ids.length
                        ? item.organization_ids.map(Number)
                        : item.organization_id != null
                            ? [Number(item.organization_id)]
                            : [];
                return ids.some((orgId) => activeOrgIds.includes(orgId));
              })
              : items;
          alive && setDataState(filtered);
        })
        .catch(() => alive && setDataState([]));
    return () => { alive = false; };
  }, [needsFetch, filterByActiveOrg, url, selectedOrgIds]);

  useEffect(() => {
    if (isEventsUpdated == true && url == 'events') {
      const data = new DataManager({
        adaptor: new WebApiAdaptor(),
        crossDomain: true,
        url: backEndUrl + url,
        headers: [{
          Authorization: `Bearer ${token}`
        }]
      });
      setDataState(data);
      dispatch(setIsEventsUpdated(false));
    }
  }, [isEventsUpdated]);

  const theRef = (args: DropDownListComponent) => {
    refDD && refDD(args);
  };

  const groupedData = useMemo(() => {
    if (!isOrgGroup || !Array.isArray(dataState)) return dataState;
    const result: any[] = [];
    dataState.forEach((item: any) => {
      const ids: number[] =
          Array.isArray(item.organization_ids) && item.organization_ids.length
              ? item.organization_ids.map(Number)
              : item.organization_id != null
                  ? [Number(item.organization_id)]
                  : [];
      if (!ids.length) {
        result.push({ ...item, __org_code: '' });
        return;
      }
      const visibleIds = selectedOrgIds.length
          ? ids.filter((id) => selectedOrgIds.includes(id))
          : ids;
      visibleIds.forEach((id) =>
          result.push({ ...item, __org_code: orgMap[String(id)] ?? String(id) })
      );
    });
    return result;
  }, [dataState, orgMap, isOrgGroup, selectedOrgIds]);

  const fieldsFinal = useMemo(
      () => (isOrgGroup ? { ...field, groupBy: '__org_code' } : field),
      [field, isOrgGroup]
  );

  const applyCollapse = (popupEl?: HTMLElement | null) => {
  if (!popupEl || !isOrgGroup) return;
  const ul = popupEl.querySelector('ul.e-list-parent');
  if (!ul) return;
  let hidden = false;
  let inTop = true;          
  const seenTop = new Set<string>();
  Array.from(ul.children).forEach((li: any) => {
    if (li.classList.contains('e-list-group-item')) {
      inTop = false;
      const key = (li.textContent || '').trim();
      hidden = collapsedRef.current.has(key);
      li.classList.toggle('org-group-collapsed', hidden);
    } else {
      li.classList.toggle('org-item-hidden', hidden);
      if (inTop) {
        const v = li.getAttribute('data-value') ?? '';
        li.classList.toggle('org-selected-dup', seenTop.has(v));
        seenTop.add(v);
      } else {
        li.classList.remove('org-selected-dup');
      }
    }
  });
  const fixed = popupEl.querySelector('.e-fixed-head') as HTMLElement | null;
  if (fixed) {
    const fixedKey = (fixed.textContent || '').trim();
    fixed.classList.toggle('org-group-collapsed', collapsedRef.current.has(fixedKey));
  }
};

  const handleOpen = (args: any) => {
    const popupEl: HTMLElement | undefined = args?.popup?.element;
    if (!popupEl || !isOrgGroup) return;

    if (!popupEl.dataset.orgCollapseBound) {
      popupEl.dataset.orgCollapseBound = '1';
      popupEl.addEventListener('click', (e: MouseEvent) => {
        const header = (e.target as HTMLElement).closest('.e-list-group-item, .e-fixed-head');
        if (!header) return;
        const key = (header.textContent || '').trim();
        if (collapsedRef.current.has(key)) collapsedRef.current.delete(key);
        else collapsedRef.current.add(key);
        applyCollapse(popupEl);
      });
    }

    observerRef.current?.disconnect();
    let scheduled = false;
    observerRef.current = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        applyCollapse(popupEl);
      });
    });
    observerRef.current.observe(popupEl, { childList: true, subtree: true });
    applyCollapse(popupEl);
  };

  const handleClose = () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  };


  return (
      <div>
        <DropDownListComponent
            id={id}
            dataSource={groupedData}
            fields={fieldsFinal}
            placeholder={placeHolder}
            ref={theRef}
            value={value}
            onChange={onChange}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            floatLabelType={floatLabelType}
            text={text}
            select={selected}
            actionComplete={actionComplete}
            open={handleOpen}
            close={handleClose}
            cssClass={isOrgGroup ? 'org-group-ddl' : undefined}
        />
      </div>
  );
};
const onFiltering = (
    dataSource: DataManager,
    searchCase: string = '',
    e: any
) => {
  let query = new Query().take(20);
  query = e.text !== '' ? query.where(searchCase, 'contains', e.text, true) : query;
  e.updateData(dataSource, query);
};
export const GlobalMultiSelection = ({
                                       field,
                                       url,
                                       id,
                                       placeHolder,
                                       refDD,
                                       onChange,
                                       value,
                                       query,
                                       allowFiltering = true,
                                       maximumSelectionLength,
                                       itemTemplate,
                                       floatLabelType,
                                       enablePersistence,
                                       actionComplete,
                                       close,
                                     }: IPropsMulti) => {
  const token = Cookies.get('token');
  const state = store.getState();
  const backEndUrl = state.search.backEndUrl;
  const [dataState, setDataState] = useState<any[]>([]);

  const fetchAll = () => {
    const fullUrl = backEndUrl + url;
    let request = optionsCache.get(fullUrl);
    if (!request) {
      const dm = new DataManager({
        adaptor: new WebApiAdaptor(),
        crossDomain: true,
        url: fullUrl,
        headers: [{
          Authorization: `Bearer ${token}`,
          contentType: 'application/json',
        }],
      });
      request = dm
          .executeQuery(new Query())
          .then((res: any) => res.result || [])
          .catch(() => {
            optionsCache.delete(fullUrl);
            return [];
          });
      optionsCache.set(fullUrl, request);
    }
    request.then((data) => setDataState(data));
  };

  useEffect(() => {
    fetchAll();
  }, [url]);

  useEffect(() => {
  const handler = (e: any) => {
    const q = e?.detail?.query;
    if (!q || url.includes(`where=${q}$`)) {
      optionsCache.delete(backEndUrl + url);
      fetchAll();
    }
  };
  window.addEventListener('globalFilterChanged', handler);
  return () => window.removeEventListener('globalFilterChanged', handler);
}, [url]);

  const dispatch = useDispatch();
  const TeamDataChanged = useSelector((state: any) => state.search.TeamDataChanged);
  const cloudSchedulerFilterTeamDropdownValueChanged = useSelector(
      (state: any) => state.search.cloudSchedulerFilterTeamDropdownValueChanged
  );

  useEffect(() => {
    if (cloudSchedulerFilterTeamDropdownValueChanged) {
      optionsCache.delete(backEndUrl + url);
      fetchAll();
      dispatch(setCloudSchedulerFilterTeamDropdownValueChanged(false));
    }
  }, [cloudSchedulerFilterTeamDropdownValueChanged]);
  useEffect(() => {
    if (TeamDataChanged) {
      optionsCache.delete(backEndUrl + url);
      fetchAll();
      dispatch(setTeamDataChanged(false));
    }
  }, [TeamDataChanged]);

  const popupElRef = useRef<HTMLElement | null>(null);
  const msRef = useRef<MultiSelectComponent | null>(null);

  const theRef = (args: MultiSelectComponent) => {
    msRef.current = args;
    refDD && refDD(args);
  };

  const groupsStoreKey = String(id ?? url);
  if (!selectedGroupsStore.has(groupsStoreKey)) {
    selectedGroupsStore.set(groupsStoreKey, new Map());
  }
  const selectedGroupsRef = {
    current: selectedGroupsStore.get(groupsStoreKey)!,
  };

  const getGroupOfLi = (li: any): string => {
    let el = li?.previousElementSibling;
    while (el) {
      if (el.classList.contains('e-list-group-item')) {
        return (el.textContent || '').trim();
      }
      el = el.previousElementSibling;
    }
    return '';
  };

  const syncDuplicateChecks = (popupEl?: HTMLElement | null) => {
    if (!popupEl || !isOrgGroup) return;
    const selected = new Set(
        ((msRef.current?.value as any[]) ?? []).map((v) => String(v))
    );
    Array.from(selectedGroupsRef.current.keys()).forEach((k) => {
      if (!selected.has(k)) selectedGroupsRef.current.delete(k);
    });
    popupEl.querySelectorAll('li.e-list-item[data-value]').forEach((li: any) => {
      const v = li.getAttribute('data-value');
      const groups = selectedGroupsRef.current.get(v);
      const on =
          selected.has(v) && (!groups || groups.has(getGroupOfLi(li)));
      li.classList.toggle('e-active', on);
      li.querySelector('.e-frame')?.classList.toggle('e-check', on);
    });
  };

  const scheduleSync = () =>
    setTimeout(() => syncDuplicateChecks(popupElRef.current), 0);

  const handleSelect = (args: any) => {
    if (isOrgGroup && args?.item && args?.itemData) {
      const v = String(args.itemData[field?.value as string]);
      const set = selectedGroupsRef.current.get(v) ?? new Set<string>();
      set.add(getGroupOfLi(args.item));
      selectedGroupsRef.current.set(v, set);
    }
    scheduleSync();
  };

  const handleRemoved = (args: any) => {
    if (isOrgGroup && args?.itemData) {
      const v = String(args.itemData[field?.value as string]);
      const g = args.item ? getGroupOfLi(args.item) : '';
      const set = selectedGroupsRef.current.get(v);
      if (set && g) {
        set.delete(g);
        if (!set.size) selectedGroupsRef.current.delete(v);
      } else {
        selectedGroupsRef.current.delete(v);
      }
    }
    scheduleSync();
  };

const handleChange = (e: any) => {
  const ms: any = msRef.current;
  const v = (ms?.value as any[]) ?? [];
  console.log(
    `SELECTED [${groupsStoreKey}]`,
    v.map((val: any) => ({
      value: val,
      text: ms?.getDataByValue?.(val)?.[field?.text as string],
      orgs: Array.from(selectedGroupsRef.current.get(String(val)) ?? []).map((code) => ({
        org_code: code,
        organization_id: Object.keys(orgMap).find((k) => orgMap[k] === code) ?? null,
      })),
    }))
  );
  onChange && onChange(e);
  scheduleSync();
};

const dbg = (tag: string) => {
  setTimeout(() => {
    const ms: any = msRef.current;
  }, 100);
};

  const orgMap = useOrgCodeMap();
  const isOrgGroup = field?.groupBy === 'organization_id';
  const [selectedOrgIds, setSelectedOrgIdsState] = useState<number[]>(() => getSelectedOrgIds());

  useEffect(() => {
    const handleOrgChanged = (e: any) => {
      const ids: number[] = e?.detail?.selectedOrgIds ?? getSelectedOrgIds();
      setSelectedOrgIdsState(ids);
    };
    window.addEventListener('organizationChanged', handleOrgChanged);
    return () => window.removeEventListener('organizationChanged', handleOrgChanged);
  }, []);

  const getItemOrgIds = (item: any): number[] => {
    if (Array.isArray(item.organization_ids) && item.organization_ids.length) {
      return item.organization_ids.map(Number);
    }
    return item.organization_id != null ? [Number(item.organization_id)] : [];
  };

  const groupedData = useMemo(() => {
    if (!isOrgGroup || !Array.isArray(dataState)) return dataState;
    const result: any[] = [];
    dataState.forEach((item: any) => {
      const ids: number[] =
          Array.isArray(item.organization_ids) && item.organization_ids.length
              ? item.organization_ids.map(Number)
              : item.organization_id != null
                  ? [Number(item.organization_id)]
                  : [];
      if (!ids.length) {
        result.push({ ...item, __org_code: '' });
        return;
      }
      const visibleIds = selectedOrgIds.length
          ? ids.filter((id) => selectedOrgIds.includes(id))
          : ids;
      visibleIds.forEach((id) =>
          result.push({ ...item, __org_code: orgMap[String(id)] ?? String(id), __organization_id: id })
      );
    });
    return result;
  }, [dataState, orgMap, isOrgGroup, selectedOrgIds]);

  const fieldsFinal = useMemo(
      () => (isOrgGroup ? { ...field, groupBy: '__org_code' } : field),
      [field, isOrgGroup]
  );


  const collapsedRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<MutationObserver | null>(null);

  const dedupeSelectedBlock = (popupEl?: HTMLElement | null) => {
  if (!popupEl || !isOrgGroup) return;
  popupEl.querySelectorAll('ul.e-list-parent').forEach((ul: any) => {
    if (ul.querySelector('.e-list-group-item')) return;
    const seen = new Set<string>();
    Array.from(ul.children).forEach((li: any) => {
      const v = li.getAttribute('data-value') ?? '';
      li.style.display = seen.has(v) ? 'none' : '';
      seen.add(v);
    });
  });
};

  const applyCollapse = (popupEl?: HTMLElement | null) => {
     if (!popupEl || !isOrgGroup) return;
  dedupeSelectedBlock(popupEl);
  const ul = popupEl.querySelector('ul.e-list-parent');
    if (!ul) return;
    let hidden = false;
    Array.from(ul.children).forEach((li: any) => {
      if (li.classList.contains('e-list-group-item')) {
        const key = (li.textContent || '').trim();
        hidden = collapsedRef.current.has(key);
        li.classList.toggle('org-group-collapsed', hidden);
      } else {
        li.classList.toggle('org-item-hidden', hidden);
      }
    });
    const fixed = popupEl.querySelector('.e-fixed-head') as HTMLElement | null;
    if (fixed) {
      const fixedKey = (fixed.textContent || '').trim();
      fixed.classList.toggle('org-group-collapsed', collapsedRef.current.has(fixedKey));
    }
  };

  const handleOpen = (args: any) => {
  const popupEl: HTMLElement | undefined = args?.popup?.element;
  if (!popupEl || !isOrgGroup) return;

  popupElRef.current = popupEl;

  if (!popupEl.dataset.orgCollapseBound) {
    popupEl.dataset.orgCollapseBound = '1';
    popupEl.addEventListener('click', (e: MouseEvent) => {
      const header = (e.target as HTMLElement).closest('.e-list-group-item, .e-fixed-head');
      if (!header) return;
      const key = (header.textContent || '').trim();
      if (collapsedRef.current.has(key)) collapsedRef.current.delete(key);
      else collapsedRef.current.add(key);
      applyCollapse(popupEl);
    });
  }

  observerRef.current?.disconnect();
  let scheduled = false;
  observerRef.current = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      applyCollapse(popupEl);
      syncDuplicateChecks(popupEl);
    });
  });
  observerRef.current.observe(popupEl, { childList: true, subtree: true });
  applyCollapse(popupEl);
  syncDuplicateChecks(popupEl);
};

  const handleClose = (args?: any) => {
  observerRef.current?.disconnect();
  observerRef.current = null;
  popupElRef.current = null;
  close && close(args);
};

  const handleDataBound = () => {
    applyCollapse(popupElRef.current);
    syncDuplicateChecks(popupElRef.current);
  };

  return (
      <div>
        <MultiSelectComponent
            id={id}
            dataSource={groupedData}
            fields={fieldsFinal}
            placeholder={placeHolder}
            ref={theRef}
            value={value}
            focus={() => dbg('focus')}
            open={(args: any) => { dbg('open'); handleOpen(args); }}
            onChange={handleChange}
            select={handleSelect}
            removed={handleRemoved}
            enableSelectionOrder={false}
            dataBound={handleDataBound}
            query={query}
            filterType="Contains"
            mode='CheckBox'
            showSelectAll={true}
            showClearButton={true}
            showDropDownIcon={true}
            floatLabelType={floatLabelType}
            allowFiltering={allowFiltering}
            maximumSelectionLength={maximumSelectionLength}
            itemTemplate={itemTemplate}
            enablePersistence={enablePersistence}
            actionComplete={actionComplete}
            close={handleClose}
        >
          <Inject services={[CheckBoxSelection]} />
        </MultiSelectComponent>
      </div>
  );
};